<script setup lang="ts">
  import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
  import Flicking, {
    type ChangedEvent,
    type FlickingEvents,
  } from '@egjs/vue3-flicking';
  import '@egjs/vue3-flicking/dist/flicking.css';
  import { createImageLoader } from '@/utils/imageLoader';

  const flicking = ref<Flicking | null>(null);
  const carouselContainer = ref<HTMLElement | null>(null);
  const carouselHeight = ref('470px');
  const isMounted = ref(true);
  const resizeObserver = ref<ResizeObserver | null>(null);

  const currentIndex = ref(0);
  const panelCount = ref(0);

  const MAX_IMAGE_HEIGHT = 470;

  function prev() {
    if (!isPrevEnabled.value) return;
    flicking.value?.prev().catch(() => {});
  }

  function next() {
    if (!isNextEnabled.value) return;
    flicking.value?.next().catch(() => {});
  }

  const isPrevEnabled = computed(() => currentIndex.value > 0);
  const isNextEnabled = computed(
    () => currentIndex.value < panelCount.value - 1
  );

  function calculateOptimalHeight() {
    if (!flicking.value || !carouselContainer.value) return;

    const panels = flicking.value.panels;
    let maxWidthToHeightRatio = 0;
    let widestSlide: HTMLElement | null = null;

    panels.forEach((panel) => {
      const img = panel.element.querySelector('img') as HTMLImageElement;
      if (img?.naturalWidth && img?.naturalHeight) {
        const ratio = img.naturalWidth / img.naturalHeight;
        if (ratio > maxWidthToHeightRatio) {
          maxWidthToHeightRatio = ratio;
          widestSlide = panel.element;
        }
      }
    });

    if (!widestSlide) return;

    const widestImg = (widestSlide as HTMLElement).querySelector(
      'img'
    ) as HTMLImageElement;
    if (widestImg?.naturalWidth && widestImg?.naturalHeight) {
      const containerStyle = window.getComputedStyle(carouselContainer.value);
      const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
      const availableWidth =
        carouselContainer.value.clientWidth - paddingLeft - paddingRight;

      const calculatedHeight =
        (availableWidth / widestImg.naturalWidth) * widestImg.naturalHeight;
      const constrainedHeight = Math.min(
        calculatedHeight,
        MAX_IMAGE_HEIGHT,
        window.innerHeight * 0.8
      );

      carouselHeight.value = `${constrainedHeight}px`;

      panels.forEach((panel) => {
        panel.element.style.height = carouselHeight.value;
      });

      nextTick(() => flicking.value?.resize());
    }
  }

  onMounted(async () => {
    resizeObserver.value = new ResizeObserver(calculateOptimalHeight);
    if (carouselContainer.value) {
      resizeObserver.value.observe(carouselContainer.value);
    }

    const imageLoader = createImageLoader({
      images: Array.from(
        carouselContainer.value?.querySelectorAll('img') ?? []
      ) as HTMLImageElement[],
    });
    const report = await imageLoader.load();
    console.log('Images loaded', report);
    calculateOptimalHeight();
  });

  onUnmounted((): void => {
    isMounted.value = false;
    resizeObserver.value?.disconnect();
  });

  function onFlickingReady(): void {
    currentIndex.value = flicking.value?.index ?? 0;
    panelCount.value = flicking.value?.panelCount ?? 0;
    calculateOptimalHeight();
  }

  function onFlickingChanged(event: ChangedEvent): void {
    currentIndex.value = event.index;
    panelCount.value = event.currentTarget?.panelCount ?? 0;
  }
</script>

<template>
  <div class="carousel-wrapper py-4 md:py-8">
    <div
      ref="carouselContainer"
      class="carousel-container overflow-hidden"
      :style="{ height: carouselHeight }"
    >
      <Flicking
        ref="flicking"
        class="w-full h-full"
        :options="{
          circular: false,
          duration: 300,
          preventDefaultOnDrag: true,
          preventClickOnDrag: true,
          bound: false,
        }"
        @ready="onFlickingReady"
        @changed="onFlickingChanged"
      >
        <slot />
      </Flicking>
    </div>

    <div class="w-full flex justify-center mt-4">
      <button
        @click="prev"
        class="nav-button prev"
        :class="{ disabled: !isPrevEnabled }"
        :disabled="!isPrevEnabled"
      >
        <span class="sr-only">Prev</span>
      </button>
      <button
        @click="next"
        class="nav-button next"
        :class="{ disabled: !isNextEnabled }"
        :disabled="!isNextEnabled"
      >
        <span class="sr-only">Next</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .carousel-wrapper {
    position: relative;
    width: 100%;

    .carousel-container {
      width: 100%;
      max-width: 100vw;
      position: relative;
      transition: height 0.3s ease-in-out;
    }

    .nav-button {
      @apply rounded-full bg-theme text-white w-10 h-10 md:w-12 md:h-12 m-2 md:m-6 flex items-center justify-center;

      &::after {
        content: '';
        @apply block w-3 md:w-4 aspect-square border-solid border-white border-r-2 md:border-r-4 border-t-2 md:border-t-4;
      }

      &.prev::after {
        @apply rotate-[-135deg] translate-x-[2px];
      }

      &.next::after {
        @apply rotate-45 -translate-x-[2px];
      }

      &.disabled {
        @apply opacity-50 cursor-not-allowed;
      }

      &:hover:not(.disabled) {
        &.prev {
          @apply animate-bounceLeft;
        }
        &.next {
          @apply animate-bounceRight;
        }
      }
    }
  }
</style>

<style lang="scss">
  .carousel-wrapper {
    .flicking-slide {
      display: flex;
      justify-content: center;
      align-items: center;

      picture {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        max-height: 100%;
        width: 100%;

        img {
          max-height: 100%;
          width: auto;
          max-width: 100%;
          object-fit: contain;
        }
      }
    }
  }
</style>
